import React, { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Textarea,
} from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faPaperPlane,
  faThumbsDown,
  faThumbsUp,
} from '@crisp/crisp-ui/icons/classic/regular';
import { trackFeedback } from '../../lib/analytics';
import styles from './FeedbackWidget.module.css';

interface FeedbackWidgetProps {
  slug: string;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ slug }) => {
  const [vote, setVote] = useState<'yes' | 'no' | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);

  const positiveTags = ['Clear & concise', 'Accurate', 'Solved my problem', 'Good examples'];
  const negativeTags = ['Missing information', 'Outdated', 'Hard to follow', 'Broken example'];

  const handleVote = (type: 'yes' | 'no') => {
    setVote(type);
    setSelectedTag('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackFeedback({
      slug,
      helpful: vote === 'yes',
      category: selectedTag || undefined,
      comment: comment.trim() || undefined,
    });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <Card className={styles.card} stylized="default">
        <CardContent className={styles.thanks}>
          <FontAwesomeIcon icon={faCheck} className="icon-slot" />
          <CardTitle>Thank you for your feedback!</CardTitle>
          <CardDescription>Your input helps us improve the Crisp documentation.</CardDescription>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader className={styles.header}>
        <div>
          <CardTitle className={styles.title}>Was this page helpful?</CardTitle>
          <CardDescription className={styles.subtitle}>
            Let us know how we can improve this document.
          </CardDescription>
        </div>
        <div className={styles.votes}>
          <Button
            type="button"
            variant={vote === 'yes' ? 'default' : 'outline'}
            size="sm"
            aria-label="Thumbs up"
            onClick={() => handleVote('yes')}
            iconPrimary={<FontAwesomeIcon icon={faThumbsUp} />}
          >
            Yes
          </Button>
          <Button
            type="button"
            variant={vote === 'no' ? 'secondary' : 'outline'}
            size="sm"
            aria-label="Thumbs down"
            onClick={() => handleVote('no')}
            iconPrimary={<FontAwesomeIcon icon={faThumbsDown} />}
          >
            No
          </Button>
        </div>
      </CardHeader>

      {vote !== null && (
        <CardContent>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.tags}>
              {(vote === 'yes' ? positiveTags : negativeTags).map((tag) => (
                <Badge
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'outline'}
                  render={
                    <button
                      type="button"
                      onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                    />
                  }
                >
                  {tag}
                </Badge>
              ))}
            </div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add optional comments or details..."
              rows={2}
            />
            <div>
              <Button
                type="submit"
                size="sm"
                iconPrimary={<FontAwesomeIcon icon={faPaperPlane} />}
              >
                Submit Feedback
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export default FeedbackWidget;
